import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "@mui/material";
import { CSSObject, styled } from "@mui/material/styles";
import { Theme, Box, Typography } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { useRecoilValue } from "recoil";
import { useQuery, UseQueryResult } from "react-query";
import { simpleQuery } from "../../services/utils";

import MenuItems from "./MenuItems";
import NavIcons from "./NavIcon";
import { Roles } from "../../AuthProvider";
import SubMenu, { menuAtom } from "./SubMenu";
import { InternalRedirection, SubMenuName } from "../interface";
import { getUnmapped } from "../../Configuration/SectionsMenu";
import { triggerUnmappeQueryAtom } from "../../Configuration/atoms";
import {
  DRAWER_WIDTH,
  DRAWER_WIDTH_OPEN,
  SUB_DRAWER_WIDTH_OPEN,
} from "../utils";
import { adminModules, menuToExclude, reportingModules } from "./utils";
import { useIntl } from "react-intl";

interface Version {
  version: string;
}

export interface RevbellMenuItem {
  id: number;
  isReactPage: boolean;
  name: string;
  translated_name: string;
  reactRoute: string;
  slug: string;
  urlPath: string;
  rang: number;
  allowedRoles: Roles[];
}

export const MenuItemLink = styled(Link)(({ theme }) => ({
  color: theme.vars.palette.common.white,
  fontSize: "1rem",
  fontWeight: 400,
  textDecoration: "none",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  "&.active": {
    color: theme.vars.palette.text.accentInverted,
  },
  "&:hover": {
    background: "transparent",
    color: theme.vars.palette.text.accentInverted,
    cursor: "pointer",
  },
  "&.disabled": {
    color: theme.vars.palette.text.disabled,
    cursor: "default",
  },
  transition:
    "background .1s ease, color .1s ease, -webkit-box-shadow .1s ease",
})) as typeof Link;

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH_OPEN,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: DRAWER_WIDTH,
});

// Shared look of every navigation drawer paper (desktop main, desktop sub,
// mobile burger) so they can't drift apart.
export const menuPaperStyles = (theme: Theme): CSSObject => ({
  border: "none",
  background: theme.vars.palette.menu.background,
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.vars.palette.action.disabled} ${theme.vars.palette.background.sunken}`,
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.vars.palette.action.disabled,
    borderRadius: "5px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.vars.palette.background.sunken,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: DRAWER_WIDTH_OPEN,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
  "& .MuiDrawer-paper": {
    ...menuPaperStyles(theme),
    justifyContent: "space-between",
    gap: theme.spacing(4.5),
  },
}));

const SubDrawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    ...menuPaperStyles(theme),
    position: "absolute",
    justifyContent: "flex-start",
    gap: theme.spacing(2),
    zIndex: 0,
  },
}));

const useMenus = (menuItems: RevbellMenuItem[]) => {
  const intl = useIntl();

  const mainMenu: RevbellMenuItem[] = [
    ...menuItems
      // Modules still flagged as PHP pages (legacy configuration…) stay in
      // the list for access checks but get no menu entry.
      .filter((menu) => menu.isReactPage)
      .filter((menu) => !menuToExclude.includes(menu.slug))
      .filter((menu) => !adminModules.includes(menu.slug))
      .filter((menu) => !reportingModules.includes(menu.slug)),
    {
      id: 6,
      isReactPage: true,
      name: "Vue hélicoptère",
      translated_name: intl.formatMessage({ id: "REPORTING_MENU" }),
      reactRoute: "/reporting/",
      slug: "cockpit",
      rang: 2,
      urlPath: "/react/helicopter/",
      allowedRoles: [Roles.User, Roles.Admin, Roles.SuperAdmin],
    },
    {
      id: -1,
      isReactPage: true,
      name: "Pilotage & Optimisation",
      translated_name: intl.formatMessage({ id: "PILOTAGE_OPTIMISATION_MENU" }),
      reactRoute: "/alerte/hebergements/",
      slug: "pilotage_optimisation",
      rang: 1.5,
      urlPath: "/alerte/hebergements/",
      allowedRoles: [Roles.User, Roles.Admin, Roles.SuperAdmin],
    },
  ];
  mainMenu.sort((a, b) => a.rang - b.rang);

  const adminMenus: InternalRedirection[] = useMemo(
    () =>
      menuItems
        .filter((menu) => !menuToExclude.includes(menu.slug))
        .filter((menu) => adminModules.includes(menu.slug))
        .map((menu) => ({
          type: "internalRedirection",
          value: menu.translated_name,
          redirectionTo: menu.reactRoute || menu.urlPath,
          menuLevel: 0,
          toTranslate: false,
        })),
    [menuItems]
  );

  const reportingMenus: InternalRedirection[] = useMemo(
    () =>
      menuItems
        .filter((menu) => !menuToExclude.includes(menu.slug))
        .filter((menu) => reportingModules.includes(menu.slug))
        .map((menu) => ({
          type: "internalRedirection",
          value: menu.translated_name,
          redirectionTo: menu.reactRoute || menu.urlPath,
          menuLevel: 1,
          toTranslate: false,
        })),
    [menuItems]
  );

  return { mainMenu, adminMenus, reportingMenus };
};

const Menu = ({ menuItems }: { menuItems: RevbellMenuItem[] }) => {
  const reloadUnmappedAtom = useRecoilValue(triggerUnmappeQueryAtom);
  const closeMainMenuTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const closeSubMenuTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const switchItemTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuState = useRecoilValue(menuAtom);
  const [open, setOpen] = useState(menuState.pinned || false);
  const [subOpen, setSubOpen] = useState(menuState.pinned || false);
  const [activeItem, setActiveItem] = useState<{
    name: string;
    translatedName?: string;
  } | null>(menuState.pinned ? { name: SubMenuName.DEFAULT } : null);
  const { mainMenu, adminMenus, reportingMenus } = useMenus(menuItems);

  const query = useQuery(["/api/unmapped/", reloadUnmappedAtom], () =>
    getUnmapped()
  );

  const handleMouseEnter = useCallback(
    (item?: string, translatedMenu?: string) => {
      if (closeMainMenuTimeout.current) {
        clearTimeout(closeMainMenuTimeout.current);
        closeMainMenuTimeout.current = null;
      }

      if (item) {
        if (switchItemTimeout.current) {
          clearTimeout(switchItemTimeout.current);
          switchItemTimeout.current = null;
        }

        if (activeItem && activeItem.name !== item) {
          switchItemTimeout.current = setTimeout(() => {
            setActiveItem({ name: item, translatedName: translatedMenu });
            setSubOpen(true);
            switchItemTimeout.current = null;
          }, 150);
        } else {
          setActiveItem({ name: item, translatedName: translatedMenu });
          setSubOpen(true);
        }
      } else {
        setOpen(true);
      }
    },
    [activeItem]
  );

  const version: UseQueryResult<Version> = useQuery(
    ["version"],
    () => simpleQuery("/api/version/", { method: "GET" }),
    { retry: false }
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent, drawer: string, isItem?: boolean) => {
      if (!menuState.pinned) {
        if (switchItemTimeout.current) {
          clearTimeout(switchItemTimeout.current);
          switchItemTimeout.current = null;
        }

        const related = e.relatedTarget;
        if (!(related instanceof HTMLElement) || !related.closest(drawer)) {
          if (closeMainMenuTimeout.current) {
            clearTimeout(closeMainMenuTimeout.current);
          }

          closeMainMenuTimeout.current = setTimeout(() => {
            setSubOpen(false);
            if (!isItem) setOpen(false);
            setActiveItem(null);
            closeMainMenuTimeout.current = null;
          }, 200);
        }
      }
    },
    [menuState.pinned]
  );

  const handleMouseEnterSubDrawer = () => {
    if (switchItemTimeout.current) {
      clearTimeout(switchItemTimeout.current);
      switchItemTimeout.current = null;
    }
    // Remove timout on subMenu
    if (closeSubMenuTimeout.current) {
      clearTimeout(closeSubMenuTimeout.current);
      closeSubMenuTimeout.current = null;
    }
    if (closeMainMenuTimeout.current) {
      clearTimeout(closeMainMenuTimeout.current);
      closeMainMenuTimeout.current = null;
    }

    setSubOpen(true);
  };

  const handleMouseLeaveSubDrawer = (e: React.MouseEvent) => {
    if (!menuState.pinned) {
      const related = e.relatedTarget as HTMLElement | null;
      let hasSubMenu =
        related instanceof HTMLElement
          ? related.closest(`[data-has-submenu="true"]`)
          : null;

      const isInMainDrawer =
        related instanceof HTMLElement && related.closest(".main-drawer");
      const isInSubDrawer =
        related instanceof HTMLElement && related.closest(".sub-drawer");

      // If hover submenu or item with submenu, remove timeout to let submenu open
      if (isInSubDrawer || hasSubMenu) {
        if (closeSubMenuTimeout.current) {
          clearTimeout(closeSubMenuTimeout.current);
          closeSubMenuTimeout.current = null;
        }
        return;
      }

      // Remove timout if exist
      if (closeSubMenuTimeout.current) {
        clearTimeout(closeSubMenuTimeout.current);
      }

      // Launch timout to close submenu
      closeSubMenuTimeout.current = setTimeout(() => {
        setSubOpen(false);
        if (!isInMainDrawer) setOpen(false);
        setActiveItem(null);
        closeSubMenuTimeout.current = null;
      }, 200);
    }
  };

  return (
    <>
      <Drawer
        onMouseEnter={() => handleMouseEnter()}
        onMouseLeave={(e) => handleMouseLeave(e, ".sub-drawer")}
        slotProps={{
          paper: {
            className: "main-drawer",
          },
        }}
        variant="permanent"
        open={open}
      >
        <MenuItems
          open={open}
          menuItems={mainMenu}
          adminMenus={adminMenus}
          onSubMenuOpen={handleMouseEnter}
          onSubMenuClose={handleMouseLeave}
          unmappedAmount={
            query.data
              ? Object.values(query.data).reduce(
                  (acc: number, val) => acc + Number(val),
                  0
                )
              : null
          }
        />
        <NavIcons
          open={open}
          onSubMenuOpen={handleMouseEnter}
          onSubMenuClose={handleMouseLeave}
        />
      </Drawer>
      <SubDrawer
        onMouseEnter={() => handleMouseEnterSubDrawer()}
        onMouseLeave={(e) => handleMouseLeaveSubDrawer(e)}
        variant="persistent"
        open={(menuState.pinned && !!activeItem) || (subOpen && !!activeItem)}
        slotProps={{
          paper: {
            sx: {
              width: SUB_DRAWER_WIDTH_OPEN,
              left: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH,
            },
            className: "sub-drawer",
          },
        }}
      >
        {activeItem && (
          <SubMenu
            activeItem={activeItem}
            adminMenus={adminMenus}
            reportingMenus={reportingMenus}
            unmappedAmount={query.data}
          />
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            verticalAlign: "bottom",
            height: "30px",
            px: 2.25,
            userSelect: "none",
            overflow: "hidden",
            marginTop: "auto",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: "menu.text",
              opacity: 0.7,
              fontWeight: 400,
              whiteSpace: "normal",
              textAlign: "center",
              lineHeight: "1.2",
            }}
          >
            {version?.data?.version}
          </Typography>
        </Box>
      </SubDrawer>
    </>
  );
};

export default Menu;
